import { useMemo } from 'react';
import { type Page } from '#shared/api/schema';
import { getConstructorPageOrder } from '../model/utils/getConstructorPageOrder';
import { groupConstructorPagesByAlias } from '../model/utils/groupConstructorPagesByAlias';
import { isConstructorPageAccount } from '../model/utils/isConstructorPageAccount';
import { isConstructorPageGiftCard } from '../model/utils/isConstructorPageGiftCard';
import { isConstructorPageLegal } from '../model/utils/isConstructorPageLegal';
import { isConstructorPageLoyalty } from '../model/utils/isConstructorPageLoyalty';
import { useVisibleConstructorPages } from '../model/utils/useVisibleConstructorPages';

export function useSortConstructorPages(pages: Page[]) {
    const availablePages = useVisibleConstructorPages(pages);

    const sortedPages = useMemo(
        () => availablePages.sort((a, b) => getConstructorPageOrder(a.alias) - getConstructorPageOrder(b.alias)),
        [availablePages]
    );

    const aliasPages = useMemo(
        () =>
            sortedPages.filter(
                (page) =>
                    page.alias &&
                    !(
                        isConstructorPageLegal(page.alias) ||
                        isConstructorPageAccount(page.alias) ||
                        isConstructorPageGiftCard(page.alias) ||
                        isConstructorPageLoyalty(page.alias)
                    )
            ),
        [sortedPages]
    );

    const aliasPagesWithTemplate = useMemo(() => groupConstructorPagesByAlias(aliasPages), [aliasPages]);

    const legalPages = useMemo(() => sortedPages.filter((page) => isConstructorPageLegal(page.alias)), [sortedPages]);

    const accountPages = useMemo(
        () => sortedPages.filter((page) => isConstructorPageAccount(page.alias)),
        [sortedPages]
    );

    const giftCardPages = useMemo(
        () => sortedPages.filter((page) => isConstructorPageGiftCard(page.alias)),
        [sortedPages]
    );

    const loyaltyPages = useMemo(
        () => sortedPages.filter((page) => isConstructorPageLoyalty(page.alias)),
        [sortedPages]
    );

    const userCreatedPages = useMemo(() => sortedPages.filter((page) => !page.alias), [sortedPages]);

    return {
        aliasPagesWithTemplate,
        legalPages,
        accountPages,
        giftCardPages,
        loyaltyPages,
        userCreatedPages,
    };
}
